import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'

import LicenseAgreementModal from './components/LicenseAgreementModal/LicenseAgreementModal'
import WhatsNewModal from './components/WhatsNewModal/WhatsNewModal'
import { routes } from './router/config'
import Router from './router/Router'
import {
  closeAlertNotificationSocket,
  createAlertNotificationSocket,
} from './store/sockets'
import { useDispatch } from './store/store'
import { get, post } from './utils/api'
import { getEnvs } from './utils/env'
import { computeLicensePdfSha256 } from './utils/licensePdfHash'
import { licenzaPdfFilename } from './utils/licenzaPdf'

const feVersion = process.env.REACT_APP_GIT_VERSION
const { backendAppVersion } = getEnvs()
const cookieKey = 'ai-go-studio-whatsnew'
const licenseCookieKey = 'ai-go-studio-license-sha256'

interface LicenseAcceptance {
  pdfSha256: string
  acceptanceDate: string
}

const App = () => {
  const [licenseModal, setLicenseModal] = useState(false)
  const [whatsNewModal, setWhatsNewModal] = useState(false)
  const [ready, setReady] = useState(false)
  const pdfSha256Ref = useRef<string>('')

  const getCookie = useCallback((key: string) => {
    const regex = new RegExp(`(^| )${key}=([^;]+)`)
    const match = document.cookie.match(regex)
    if (match) {
      return match[2]
    }
    return undefined
  }, [])

  const setCookie = useCallback((key: string, value: string | undefined) => {
    const expireDate = new Date()
    expireDate.setFullYear(expireDate.getFullYear() + 10)
    document.cookie = `${key}=${value}; path=/; expires=${expireDate.toUTCString()};`
  }, [])

  const writeWhatsNewCookie = useCallback(() => {
    const cookieValue = getCookie(cookieKey)
    const currentCookieValue = `${feVersion}${backendAppVersion}`
    if (!cookieValue || cookieValue !== currentCookieValue) {
      setCookie(cookieKey, currentCookieValue)
    }
  }, [getCookie, setCookie])

  const checkAndShowWhatsNew = useCallback(() => {
    const cookieValue = getCookie(cookieKey)
    const currentCookieValue = `${feVersion}${backendAppVersion}`
    if (!cookieValue || cookieValue !== currentCookieValue) {
      setWhatsNewModal(true)
    } else {
      setReady(true)
    }
  }, [getCookie])

  useEffect(() => {
    const checkLicense = async () => {
      // In Cypress: skip license check, mount Router immediately with WhatsNew as overlay
      if ('Cypress' in window) {
        setReady(true)
        setWhatsNewModal(true)
        return
      }

      let sha256: string
      try {
        sha256 = await computeLicensePdfSha256()
        pdfSha256Ref.current = sha256
      } catch {
        setLicenseModal(true)
        return
      }

      const cookieSha256 = getCookie(licenseCookieKey)

      try {
        const acceptance = await get<LicenseAcceptance>('/license_acceptance/')
        if (acceptance.pdfSha256 === sha256) {
          setCookie(licenseCookieKey, sha256)
          checkAndShowWhatsNew()
        } else {
          setLicenseModal(true)
        }
      } catch (err) {
        const status = (err as AxiosError)?.response?.status
        if (status === 404) {
          // No acceptance record yet
          setLicenseModal(true)
        } else if (cookieSha256) {
          // Auth/network error: fall back to cookie
          checkAndShowWhatsNew()
        } else {
          setLicenseModal(true)
        }
      }
    }

    checkLicense()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLicenseConfirm = useCallback(async () => {
    const sha256 = pdfSha256Ref.current
    setLicenseModal(false)
    try {
      await post('/license_acceptance/', {
        pdfSha256: sha256,
        pdfFilename: licenzaPdfFilename,
      })
      setCookie(licenseCookieKey, sha256)
    } catch {
      // Store only in cookie if API fails
      setCookie(licenseCookieKey, sha256)
    }
    checkAndShowWhatsNew()
  }, [checkAndShowWhatsNew, setCookie])

  const handleWhatsNewClose = useCallback(() => {
    setWhatsNewModal(false)
    writeWhatsNewCookie()
    setReady(true)
  }, [writeWhatsNewCookie])

  const dispatch = useDispatch()
  useEffect(() => {
    const socket = createAlertNotificationSocket(dispatch)

    return () => {
      closeAlertNotificationSocket(socket)
    }
  }, [dispatch])

  return (
    <>
      {licenseModal && (
        <LicenseAgreementModal onConfirm={handleLicenseConfirm} />
      )}
      {whatsNewModal && <WhatsNewModal onCloseRequest={handleWhatsNewClose} />}
      {ready && <Router routes={routes} />}
    </>
  )
}

export default App
